import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { DEFAULT_HOME } from '@/auth/routes'
import { Button } from '@/ui/Button'
import { InlineError } from '@/ui/states'
import { PublicPage } from './PublicPage'
import { useToken } from './useToken'

/**
 * `/verify-email-change` — the link `email_change_link()` builds.
 *
 * **A separate page from `/verify-email` on purpose**, and the reason is not
 * tidiness. Both links carry a `token`, and the two token kinds have opposite
 * address rules: one requires the account to still hold the address, the other
 * requires it not to. A single page could not tell which kind it held without
 * asking the server first, and that question is an unauthenticated lookup
 * reporting whether a token exists — an oracle. Two paths, two posts, no
 * oracle (`api/app/mail/messages.py`).
 *
 * The person opening this link holds the **new** mailbox, which is very often
 * not the browser the change was requested from. So it is public, and success
 * does not assume a session.
 */
export function VerifyEmailChange(): JSX.Element {
  const token = useToken()
  const navigate = useNavigate()
  const { status, refresh } = useAuth()

  const verify = useMutation((body: { token: string }) =>
    call(() => api.POST('/api/v1/auth/email/change/verify', { body })),
  )

  // The address that is now on the account, read off the response. Worth
  // showing: whoever opened this link may hold several mailboxes, and "this is
  // now the address" without naming it is the kind of confirmation people
  // re-read three times.
  const [confirmed, setConfirmed] = useState<string | null>(null)

  const attempted = useRef(false)
  const { run } = verify
  useEffect(() => {
    if (!token || attempted.current) return
    attempted.current = true
    void (async () => {
      const result = await run({ token })
      if (!result.ok) return
      setConfirmed(result.data.user.email ?? null)
      if (status === 'authenticated') await refresh()
    })()
  }, [token, run, status, refresh])

  if (!token) {
    return (
      <PublicPage title="That link is incomplete">
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            The address in your browser has no confirmation code in it. Ask for the change again
            from Settings and open the newest message.
          </p>
          <Link className="ar-btn ar-btn--primary" to="/login">
            Sign in
          </Link>
        </div>
      </PublicPage>
    )
  }

  if (verify.state.status === 'done') {
    return (
      <PublicPage title="Address changed">
        <div className="ar-card__body ar-stack">
          <p className="ar-badge ar-badge--success" style={{ marginTop: 0 }}>
            <span aria-hidden="true">✓ </span>
            {confirmed ?? 'That address'} is now the one on your account
          </p>
          <p className="ar-muted">
            Sign in with it from now on. The previous address no longer works.
          </p>
          {status === 'authenticated' ? (
            <Button
              tone="primary"
              onClick={() => {
                void navigate(DEFAULT_HOME)
              }}
            >
              Continue to AugRank
            </Button>
          ) : (
            <Link className="ar-btn ar-btn--primary" to="/login">
              Sign in
            </Link>
          )}
        </div>
      </PublicPage>
    )
  }

  if (verify.state.status === 'error') {
    return (
      <PublicPage title="That link no longer works">
        <div className="ar-card__body ar-stack">
          <InlineError failure={verify.state.failure} />
          <p className="ar-muted" style={{ marginTop: 0 }}>
            Confirmation links can be used once, expire, and stop working if the address was taken
            by another account in the meantime. Your existing address is unchanged.
          </p>
          <Link className="ar-btn ar-btn--primary" to="/login">
            Sign in and try again
          </Link>
        </div>
      </PublicPage>
    )
  }

  return (
    <PublicPage title="Confirming your new address">
      <div className="ar-card__body ar-row" aria-busy="true">
        <span className="ar-spinner" aria-hidden="true" />
        <span className="ar-muted">One moment.</span>
      </div>
    </PublicPage>
  )
}
